import { WizardState } from '@/app/(intakeFormV2)/intake-v2/types';
import { EMAIL_REGEX } from '@/utils/emailRegex';
import { isPediatricsEligible } from '@/utils/pediatrics';

const hasEligiblePediatricsMember = (state: WizardState): boolean => {
  const members = state.formData?.members || [];
  return members.some(member => {
    if (!member.date_of_birth) return false;
    return isPediatricsEligible(member.date_of_birth);
  });
};

export type TIntakeStepValidationResult =
  | {
      isValid: true;
      errors: undefined;
    }
  | { isValid: false; errors: Record<string, string> };

export type TIntakeStep = {
  id: string;
  slug: string;
  title: string;
  formsortFlow?: string;
  formsortVariant?: string;
  isVisible: (state: WizardState) => boolean;
  validate: (state: WizardState) => TIntakeStepValidationResult;
  parent?: string;
};

export const INTAKE_STEPS: TIntakeStep[] = [
  {
    id: 'welcome',
    slug: 'welcome',
    title: 'Get your personalized quote in minutes.',
    isVisible: () => true,
    validate: (state: WizardState) => {
      if (!state.formData?.signup_type) {
        return {
          isValid: false,
          errors: { signup_type: 'Please select an option' },
        };
      }

      return { isValid: true, errors: undefined };
    },
  },
  {
    id: 'signup-type',
    slug: 'signup-type',
    title: 'Who are you signing up?',
    isVisible: () => true,
    validate: (state: WizardState) => {
      if (!state.formData?.signup_type) {
        return {
          isValid: false,
          errors: { signup_type: 'Please select an option' },
        };
      }

      return { isValid: true, errors: undefined };
    },
  },
  {
    id: 'zip-code',
    slug: 'zip-code',
    title: 'Where do you live?',
    isVisible: () => true,
    validate: (state: WizardState) => {
      const zipCode = state.formData?.zip_code?.trim();
      if (!zipCode) {
        return {
          isValid: false,
          errors: { zip_code: 'ZIP code is required' },
        };
      }

      if (!/^\d{5}(-\d{4})?$/.test(zipCode)) {
        return {
          isValid: false,
          errors: { zip_code: 'Please enter a valid US ZIP code' },
        };
      }
      return { isValid: true, errors: undefined };
    },
  },
  {
    id: 'location-check',

    slug: 'location-check',
    title: 'Your Nearest Location',
    isVisible: () => true,
    validate: () => ({ isValid: true, errors: undefined }),
  },
  {
    id: 'reasons',
    slug: 'reasons',
    title: 'What brings you to Sollis?',
    isVisible: () => true,
    validate: (state: WizardState) => {
      const reasons = state.formData?.reasons || [];
      if (reasons.length === 0) {
        return {
          isValid: false,
          errors: { reasons: 'Please select at least one option' },
        };
      }
      return { isValid: true, errors: undefined };
    },
  },
  {
    id: 'urgency',
    slug: 'urgency',
    title: 'When do you want to start this membership?',
    isVisible: () => true,
    validate: (state: WizardState) => {
      if (!state.formData?.urgency) {
        return {
          isValid: false,
          errors: { urgency: 'Please select an option' },
        };
      }
      return { isValid: true, errors: undefined };
    },
  },
  {
    id: 'pcp-status',
    slug: 'pcp-status',
    title: 'Primary Care Physician',
    isVisible: () => true,
    validate: (state: WizardState) => {
      if (!state.formData?.pcp_status) {
        return {
          isValid: false,
          errors: { pcp_status: 'Please select an option' },
        };
      }
      return { isValid: true, errors: undefined };
    },
  },
  {
    id: 'contact-info',
    slug: 'contact-info',
    title: 'Contact Information',
    isVisible: () => true,
    validate: (state: WizardState) => {
      const errors: Record<string, string> = {};
      const contact = state.formData?.contact_details;

      if (!contact) {
        return {
          isValid: false,
          errors: { contact_details: 'Contact details are required' },
        };
      }

      if (!contact.first_name?.trim()) {
        errors.first_name = 'First name is required';
      }

      if (!contact.last_name?.trim()) {
        errors.last_name = 'Last name is required';
      }

      if (!contact.email?.trim()) {
        errors.email = 'Email is required';
      } else if (!EMAIL_REGEX.test(contact.email)) {
        errors.email = 'Please enter a valid email address';
      }

      if (!contact.phone?.trim()) {
        errors.phone = 'Phone number is required';
      } else {
        const cleaned = contact.phone.replace(/\D/g, '');
        if (!(
          cleaned.length === 10 ||
          (cleaned.length === 11 && cleaned[0] === '1')
        )) {
          errors.phone = 'Please enter a valid phone number';
        }
      }

      if (Object.keys(errors).length) {
        return { isValid: false, errors };
      }

      return { isValid: true, errors: undefined };
    },
  },
  {
    id: 'member-details',
    slug: 'member-details',
    title: 'Member Details',
    isVisible: () => true,
    validate: (state: WizardState) => {
      const errors: Record<string, string> = {};
      const members = state.formData?.members;

      if (!members || members.length === 0) {
        errors.members = 'At least one member is required';
        return { isValid: false, errors };
      }

      members.forEach((member, index) => {
        if (!member.first_name?.trim()) {
          errors[`member_${index}_first_name`] = 'First name is required';
        }
        if (!member.last_name?.trim()) {
          errors[`member_${index}_last_name`] = 'Last name is required';
        }
        if (!member.date_of_birth) {
          errors[`member_${index}_date_of_birth`] = 'Date of birth is required';
        } else {
          const dob = new Date(member.date_of_birth);
          const today = new Date();
          const minDate = new Date(today.getFullYear() - 120, 0, 1);
          if (isNaN(dob.getTime()) || dob > today || dob < minDate) {
            errors[`member_${index}_date_of_birth`] =
              'Please enter a valid date of birth';
          }
        }
      });

      if (Object.keys(errors).length) {
        return { isValid: false, errors };
      }

      return { isValid: false, errors };
    },
  },
  {
    id: 'pediatrics-upsell',
    slug: 'pediatrics-upsell',
    title: 'Sollis Pediatrics',
    isVisible: state =>
      state.formData?.location_has_pediatrics === true &&
      hasEligiblePediatricsMember(state),
    validate: () => ({ isValid: true, errors: undefined }),
  },
  {
    id: 'quote',
    slug: 'quote',
    title: 'Your Quote',
    isVisible: () => true,
    validate: () => ({ isValid: true, errors: undefined }),
  },
  {
    id: 'quote-edit',
    slug: 'quote-edit',
    title: 'Your Quote',
    isVisible: () => false,
    validate: () => ({ isValid: true, errors: undefined }),
    parent: 'quote',
  },
  {
    id: 'quote-members',
    slug: 'quote-members',
    title: 'Your Quote',
    isVisible: () => false,
    validate: () => ({ isValid: true, errors: undefined }),
    parent: 'quote',
  },
];

export const getStepBySlug = (slug: string): TIntakeStep | undefined =>
  INTAKE_STEPS.find(step => step.slug === slug);

export const getChildSteps = (): TIntakeStep[] =>
  INTAKE_STEPS.filter(step => Boolean(step.parent));

export const getVisibleSteps = (state: WizardState): TIntakeStep[] => {
  return INTAKE_STEPS.filter(step => step.isVisible(state));
};

export const getVisibleStepIndex = (
  slug: string,
  state: WizardState
): number => {
  const visibleSteps = getVisibleSteps(state);
  const index = visibleSteps.findIndex(step => step.slug === slug);

  if (index > -1) return index;

  const childSteps = getChildSteps();
  const child = childSteps.find(step => step.slug === slug);

  return visibleSteps.findIndex(step => step.slug === child?.parent);
};

export const getFirstIncompleteStep = (
  state: WizardState
): TIntakeStep | undefined =>
  getVisibleSteps(state).find(
    step => !state.completedSteps.includes(step.slug)
  );

export const areAllStepsCompleted = (state: WizardState): boolean => {
  const visibleSteps = getVisibleSteps(state);

  return visibleSteps.every(step => state.completedSteps.includes(step.slug));
};
